import {
  type CSSProperties,
  type RefObject,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore framer-motion is in the parent dir
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronRightIcon } from 'lucide-react';

import { Button } from 'components/Button';
import { Flex } from 'components/layout';
import { Typography } from 'components/typography';
import { ServiceContext } from 'contexts/serviceContext';
import { Maybe } from 'types/UtilityTypes';
import { getDrumkitContainer } from 'utils/getDrumkitContainer';
import { cn } from 'utils/shadcn';

import { useQuickQuoteCurrency } from '../hooks/useQuickQuoteCurrency';
import { CarrierCostType, ProfitType } from '../types';
import {
  buildSellRateFormula,
  formatStickySellRate,
} from '../utils/buildSellRateFormula';

type FloatingSellRateProps = {
  // IntersectionObserver target (Rate Breakdown).
  rateBreakdownRef: RefObject<Maybe<HTMLDivElement>>;
  finalPrice: Maybe<number>;
  carrierCost: number;
  carrierCostType: CarrierCostType;
  profit: number;
  profitType: ProfitType;
  // When false, the floating footer is never shown (e.g. LTL / no quote yet)
  enabled: boolean;
};

const SIDEBAR_INSET_PX = 8;
const RATE_BREAKDOWN_VISIBILITY_THRESHOLD = 0.2;

function isRateBreakdownInView(el: HTMLElement): boolean {
  const rect = el.getBoundingClientRect();
  if (rect.height <= 0) {
    return false;
  }

  const visibleTop = Math.max(rect.top, 0);
  const visibleBottom = Math.min(rect.bottom, window.innerHeight);
  const visibleHeight = Math.max(0, visibleBottom - visibleTop);

  return visibleHeight / rect.height >= RATE_BREAKDOWN_VISIBILITY_THRESHOLD;
}

// Show the sticky footer only when the calculator is below the view,
// never when in view or when the user has scrolled past it.
function shouldShowFloatingComponent(el: HTMLElement): boolean {
  if (isRateBreakdownInView(el)) {
    return false;
  }

  const rect = el.getBoundingClientRect();
  if (rect.top < 0) {
    return false;
  }

  return true;
}

function fixedPositionStyles(containerEl: Maybe<Element>): CSSProperties {
  const rect = containerEl?.getBoundingClientRect();
  const width = rect
    ? Math.max(rect.width - SIDEBAR_INSET_PX * 2, 240)
    : undefined;

  return {
    position: 'fixed',
    bottom: 10,
    left: rect ? rect.left + rect.width / 2 : '50%',
    zIndex: 60,
    width,
    maxWidth: width,
  };
}

// Floating sell-rate footer for Quick Quote. Mirrors Kit's ScrollToTopPill (portal +
// fixed position), but stays at the bottom while the Rate Breakdown / calculator is
// out of view so users can see how card selection changes the final sell without scrolling.
export function FloatingSellRate({
  rateBreakdownRef,
  finalPrice,
  carrierCost,
  carrierCostType,
  profit,
  profitType,
  enabled,
}: FloatingSellRateProps) {
  const {
    serviceFeaturesEnabled: { isQuoteCalculatorMarginEnabled },
  } = useContext(ServiceContext);
  const { isEnabled, formatAmount } = useQuickQuoteCurrency();
  const currencyFormatter = isEnabled ? formatAmount : undefined;

  const portalContainer = getDrumkitContainer();
  const [style, setStyle] = useState<CSSProperties>(() =>
    fixedPositionStyles(portalContainer)
  );
  const [showFloating, setShowFloating] = useState(false);

  const hasSellRate =
    finalPrice != null && Number.isFinite(finalPrice) && finalPrice > 0;

  const shouldShow = enabled && hasSellRate && showFloating;

  const formula = useMemo(
    () =>
      buildSellRateFormula({
        carrierCost,
        profit,
        profitType,
        carrierCostType,
        isMarginEnabled: isQuoteCalculatorMarginEnabled,
        formatAmount: currencyFormatter,
      }),
    [
      carrierCost,
      profit,
      profitType,
      carrierCostType,
      isQuoteCalculatorMarginEnabled,
      currencyFormatter,
    ]
  );

  const sellLabel = hasSellRate
    ? formatStickySellRate(finalPrice, carrierCostType, currencyFormatter)
    : '';

  const scrollToRateBreakdown = () => {
    rateBreakdownRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const el = rateBreakdownRef.current;
    if (!el) {
      return;
    }

    const updateShowFloating = (target: HTMLElement) => {
      setShowFloating(shouldShowFloatingComponent(target));
    };

    // Seed before the observer's first async callback to avoid a footer flash.
    updateShowFloating(el);

    const observer = new IntersectionObserver(
      ([entry]) => {
        updateShowFloating(entry.target as HTMLElement);
      },
      { root: null, threshold: RATE_BREAKDOWN_VISIBILITY_THRESHOLD }
    );

    observer.observe(el);

    // Intersection ratio stays 0 when scrolling from below → above the viewport;
    // re-check on scroll so we hide once the user has scrolled past the calculator.
    const onScroll = () => {
      updateShowFloating(el);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', onScroll);
    };
  }, [enabled, rateBreakdownRef]);

  useEffect(() => {
    if (!shouldShow) {
      return;
    }

    const update = () => setStyle(fixedPositionStyles(getDrumkitContainer()));
    update();

    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, { passive: true });
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update);
    };
  }, [shouldShow]);

  if (!portalContainer) {
    return null;
  }

  return createPortal(
    <AnimatePresence>
      {shouldShow && (
        <motion.div
          key='qq-floating-sell-rate'
          style={style}
          initial={{ y: 48, opacity: 0, x: '-50%' }}
          animate={{ y: 0, opacity: 1, x: '-50%' }}
          exit={{ y: 48, opacity: 0, x: '-50%' }}
          transition={{ type: 'spring', stiffness: 420, damping: 28 }}
          className={cn(
            'rounded-xl border border-brand-500',
            'bg-brand-50 px-3.5 py-3 shadow-lg'
          )}
        >
          <Flex direction='col' gap='sm' className='w-full'>
            <Flex align='start' justify='between' gap='sm' className='w-full'>
              <Typography
                variant='body-sm'
                weight='semibold'
                className='text-neutral-900 shrink-0'
              >
                Sell rate
              </Typography>
              <Flex direction='col' align='end' gap='xs' className='min-w-0'>
                <Typography
                  variant='h4'
                  weight='bold'
                  className='text-brand-500 tabular-nums leading-none'
                >
                  {sellLabel}
                </Typography>
                <Typography
                  variant='body-xxs'
                  className='text-neutral-500 tabular-nums text-right'
                >
                  {formula}
                </Typography>
              </Flex>
            </Flex>
            <Button
              type='button'
              size='auto'
              className='h-7 w-full px-3'
              buttonNamePosthog='qq_sticky_finish_rate'
              onClick={scrollToRateBreakdown}
              aria-label={`Finish rate. Current sell ${sellLabel} from ${formula}.`}
            >
              <Flex align='center' justify='center' gap='xs'>
                <Typography
                  variant='body-xs'
                  weight='medium'
                  className='text-neutral-50 text-[13px]'
                >
                  Finish rate
                </Typography>
                <ChevronRightIcon
                  className='stroke-1.5 h-4 w-4 !stroke-neutral-50'
                  aria-hidden
                />
              </Flex>
            </Button>
          </Flex>
        </motion.div>
      )}
    </AnimatePresence>,
    portalContainer
  );
}
